import { useCallback, useEffect, useMemo, useRef, type ReactNode } from "react";
import { SEEDED_EMPTY_QUERY } from "#app/lib/filters/seeded-empty-query";
import {
  Box,
  Button,
  Checkbox,
  OptionPanelRow,
  Popover,
  PopoverPopup,
  PopoverTrigger,
  ScrollArea,
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
  Text,
  WidgetControlsProvider,
  type WidgetControlProps,
  type WidgetControls,
  type WidgetSettingOption,
} from "@salestrak/ui";
import { EntityTag } from "#app/components/entity";
import { AdvancedFilters } from "#app/components/filters/advanced-filters";
import { DateRangePresetPicker } from "#app/components/filters/date-range-preset-picker";
import { FilterToggle } from "#app/components/filters/filter-toggle";
import { issueFilterConfig } from "#app/lib/filters/configs/issue-filter-config";
import { countActiveFilters } from "#app/lib/filters/build-where";
import type { FilterState } from "#app/lib/filters/types";
import { useCustomFieldsList } from "#app/lib/hooks/use-custom-fields-list";
import { useFilters } from "#app/lib/hooks/use-filters";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { MIN_CONVERSION_STEPS } from "#app/widgets/definitions/stage-conversion/use-stage-conversion";
import { formatPresetValueLabel } from "#app/lib/utils/date-presets";
import { STAGES_SOURCE } from "#app/widgets/definitions/projects-by-stage/stage-source";
// Direct module paths into the dashboards feature (never its barrel — the
// barrel leads back to the registry, which this file's consumers sit beside).
import { parseWidgetFilters, type WidgetFilters } from "#app/widgets/dashboards/widget-filters";
import { useDashboardIssueScope, useDashboardScope } from "#app/widgets/dashboards/scope/dashboard-scope-context";
import {
  DIMENSION_FIELDS_SOURCE,
  MEASURE_FIELDS_SOURCE,
  useDimensionFieldOptions,
  useFieldOptionLabels,
  useMeasureFieldOptions,
} from "#app/widgets/dashboards/widget-measure";

/**
 * The app's binding of the widget kit's control-registry seam. It supplies the
 * domain-specific settings pieces the generic {@link SettingsFields} renderer
 * dispatches to: custom controls (`issueFilters`, `source`, `workflowSource`,
 * `stageSteps`) and the
 * option sources for `dynamicOptions` selects: `customFields` / `workflows`, and
 * the two chart pickers — `dimensionFields` (the groupable custom fields the
 * Group by select lists after the built-in dimensions) and `measureFields` (the
 * measurable fields the Measure select lists after the count, and the Compare
 * with multiselect offers).
 *
 * Mount it inside `<WidgetRegistryProvider>` in the workspace layout. Adding a new
 * domain field kind is a new entry here — the kit never changes.
 */
export function AppWidgetControls({ children }: { children: ReactNode }) {
  // The names behind the two pickers' stored values, for the titles a widget
  // derives ("Deductible by Bid Type") — the option sources above are hooks
  // the settings drawer calls; a title needs the lookup outside any drawer.
  const fieldLabels = useFieldOptionLabels();
  const stageLabel = useStageOptionLabel();
  const optionLabels = useMemo(
    () => ({ ...fieldLabels, [STAGES_SOURCE]: stageLabel }),
    [fieldLabels, stageLabel],
  );
  const value = useMemo<WidgetControls>(
    () => ({
      controls: {
        issueFilters: IssueFiltersControl,
        source: SourceControl,
        workflowSource: WorkflowSourceControl,
        stageSteps: StageStepsControl,
      },
      optionSources: {
        customFields: useCustomFieldsAsOptions,
        workflows: useWorkflowsAsOptions,
        [STAGES_SOURCE]: useStagesAsOptions,
        [DIMENSION_FIELDS_SOURCE]: useDimensionFieldOptions,
        [MEASURE_FIELDS_SOURCE]: useMeasureFieldOptions,
      },
      optionLabels,
    }),
    [optionLabels],
  );
  return <WidgetControlsProvider value={value}>{children}</WidgetControlsProvider>;
}

/** The surrounding dashboard's pinned entity, resolved to a renderable chip:
 * the workflow or team the dashboard is scoped to, or null off-scope (and
 * outside any dashboard). */
function useDashboardScopeEntity() {
  const scope = useDashboardScope();
  const workspace = useWorkspaceData();
  const scopedEntity = scope?.entity;
  return useMemo(() => {
    if (scopedEntity?.type === "workflow") {
      const workflow = workspace.workflowMap.get(scopedEntity.id);
      return workflow ? { kind: "workflow" as const, id: scopedEntity.id, workflow } : null;
    }
    if (scopedEntity?.type === "team") {
      const team = workspace.teamMap.get(scopedEntity.id);
      return team ? { kind: "team" as const, id: scopedEntity.id, team } : null;
    }
    return null;
  }, [scopedEntity?.id, scopedEntity?.type, workspace.teamMap, workspace.workflowMap]);
}

/** `kind: "source"` — the read-only "where does this widget's data come from"
 * row: the dashboard's pinned entity as a chip, or "All data" off-scope. */
function SourceControl() {
  const entity = useDashboardScopeEntity();

  return (
    <Box
      width="100%"
      height={32}
      surface="card"
      rounded="md"
      border
      paddingX="sm"
      direction="row"
      align="center"
      justify="start"
    >
      {entity ? (
        <EntityTag entity={entity} display="full" interactive={false} />
      ) : (
        <Text size="xs" variant="muted">
          All data
        </Text>
      )}
    </Box>
  );
}

/**
 * `kind: "workflowSource"` — the writable sibling of `source`: a widget's
 * Workflow picker whose RESTING face is the widget's effective source. A chosen
 * workflow shows as its chip; while the config is clear the face shows whatever
 * the dashboard itself is scoped to — the pinned entity's chip, or "All data"
 * off-scope — because a clear config already follows the dashboard's scope in
 * the data hooks. The popup leads with that Default above the LIVE workflows:
 * an archived workflow is listed only while it is the stored choice, so the
 * face still names it and the row can be moved off it.
 *
 * Inside a dashboard pinned to ONE workflow (a workflow's Dashboard tab) the
 * row is locked: it reads as the read-only Source face naming that workflow,
 * and a stored id that names any other workflow is cleared, because the data
 * hooks AND-merge the choice with the scope and a disagreement reads as empty.
 * A workspace dashboard (scoped to a workflow set or off-scope) keeps the
 * picker.
 */
function WorkflowSourceControl({ field, value, disabled, onCommit }: WidgetControlProps) {
  const { workflows: allWorkflows, workflowMap } = useWorkspaceData();
  const scopeEntity = useDashboardScopeEntity();
  const lockedWorkflowId = scopeEntity?.kind === "workflow" ? scopeEntity.id : null;
  // A stored id whose workflow is gone reads as clear, like the map that
  // resolves it: the face falls back to the dashboard's scope.
  const chosenWorkflow =
    typeof value === "string" && value !== "" ? workflowMap.get(value) : undefined;
  const chosenId = chosenWorkflow?.workflowId;

  const staleChoice =
    lockedWorkflowId != null &&
    typeof value === "string" &&
    value !== "" &&
    value !== lockedWorkflowId;
  useEffect(() => {
    if (staleChoice && !disabled) onCommit(field.key, null);
  }, [staleChoice, disabled, field.key, onCommit]);

  const workflows = useMemo(
    () => allWorkflows.filter((w) => !w.archived || w.workflowId === chosenId),
    [allWorkflows, chosenId],
  );

  const items = useMemo(
    () => [
      { value: "", label: "Default" },
      ...workflows.map((w) => ({ value: w.workflowId, label: w.name ?? "Untitled workflow" })),
    ],
    [workflows],
  );

  if (lockedWorkflowId != null) return <SourceControl />;

  return (
    <Select
      items={items}
      value={chosenWorkflow?.workflowId ?? ""}
      onValueChange={(v) => onCommit(field.key, v == null || v === "" ? null : v)}
    >
      <SelectTrigger aria-label={field.label} disabled={disabled}>
        <SelectValue>
          {() =>
            chosenWorkflow ? (
              <EntityTag
                entity={{
                  kind: "workflow",
                  id: chosenWorkflow.workflowId,
                  workflow: chosenWorkflow,
                }}
                display="full"
                interactive={false}
              />
            ) : scopeEntity ? (
              <EntityTag entity={scopeEntity} display="full" interactive={false} />
            ) : (
              <Text size="xs" variant="muted">
                All data
              </Text>
            )
          }
        </SelectValue>
      </SelectTrigger>
      <SelectPopup>
        <SelectItem value="" label="Default" description="Follows the dashboard">
          Default
        </SelectItem>
        {workflows.map((w) => (
          <SelectItem
            key={w.workflowId}
            value={w.workflowId}
            label={w.name ?? "Untitled workflow"}
            size="lg"
          >
            <EntityTag
              entity={{ kind: "workflow", id: w.workflowId, workflow: w }}
              display="full"
              interactive={false}
            />
          </SelectItem>
        ))}
      </SelectPopup>
    </Select>
  );
}


/**
 * `kind: "stageSteps"` — the steps of a pipeline read, as a checklist of one
 * workflow's stages in stage order. The workflow is `field.props.workflowId`
 * (the widget's own choice), else the dashboard's pinned workflow; with
 * neither the row says what to do first. Canceled stages are never steps and
 * are not listed. The stored value is `null` while every stage is in (the
 * default, so a new stage joins the strip on its own) and the picked ids
 * otherwise; the list cannot drop under {@link MIN_CONVERSION_STEPS}, and
 * "Reset" returns it to null.
 */
function StageStepsControl({ field, value, disabled, onCommit }: WidgetControlProps) {
  const { stagesByWorkflow, workflowMap } = useWorkspaceData();
  const scopeEntity = useDashboardScopeEntity();
  const own = field.props?.workflowId;
  const workflowId =
    typeof own === "string" && own !== "" && workflowMap.has(own)
      ? own
      : scopeEntity?.kind === "workflow"
        ? scopeEntity.id
        : null;
  const stages = useMemo(
    () => (workflowId ? (stagesByWorkflow.get(workflowId) ?? []) : []).filter((s) => s.type !== "canceled"),
    [stagesByWorkflow, workflowId],
  );
  const chosen = Array.isArray(value)
    ? new Set(value.filter((v): v is string => typeof v === "string"))
    : null;
  const picked = chosen ? stages.filter((s) => chosen.has(s.stageId)) : stages;
  const pickedIds = new Set(picked.map((s) => s.stageId));

  const commit = (next: Set<string>) => {
    const inStageOrder = stages.filter((s) => next.has(s.stageId)).map((s) => s.stageId);
    onCommit(field.key, inStageOrder.length === stages.length ? null : inStageOrder);
  };

  if (!workflowId) {
    return (
      <Text size="xs" variant="muted">
        Choose a workflow to pick its steps.
      </Text>
    );
  }

  return (
    <Box direction="column" gap="2xs">
      {stages.map((stage) => {
        const on = pickedIds.has(stage.stageId);
        // The floor holds: the last two steps cannot be unchecked.
        const locked = on && pickedIds.size <= MIN_CONVERSION_STEPS;
        return (
          <OptionPanelRow
            key={stage.stageId}
            label={
              <EntityTag
                entity={{ kind: "stage", id: stage.stageId, stage }}
                display="full"
                interactive={false}
              />
            }
            disabled={disabled}
          >
            <Checkbox
              aria-label={stage.name}
              checked={on}
              disabled={disabled || locked}
              onCheckedChange={(checked) => {
                const next = new Set(pickedIds);
                if (checked) next.add(stage.stageId);
                else next.delete(stage.stageId);
                commit(next);
              }}
            />
          </OptionPanelRow>
        );
      })}
      {chosen ? (
        <Box direction="row" justify="end">
          <Button variant="ghost" size="xs" disabled={disabled} onClick={() => onCommit(field.key, null)}>
            Reset to all stages
          </Button>
        </Box>
      ) : null}
    </Box>
  );
}

/** The advanced builder opens showing one empty row, not a bare "Add filter"
 * button — the same seed the query resets to on clear. */

/**
 * `kind: "issueFilters"` — a widget's own Filters row, mirroring the dashboard
 * toolbar's chip language: an advanced query-builder popover, a date-range
 * preset chip, and an archived flag chip. The stored value is ONE
 * `WidgetFilters` blob under the field's key (see `widget-filters.ts`); each
 * chip commits a partial patch of it. The widget's data hook and the Data
 * drill-down both fold the same blob via `useWidgetFilters`.
 */
function IssueFiltersControl({ field, value, disabled, onCommit }: WidgetControlProps) {
  const issueScope = useDashboardIssueScope();
  const filters = useMemo(() => parseWidgetFilters(value), [value]);

  const commitPatch = useCallback(
    (patch: Partial<WidgetFilters>) => {
      const current = value && typeof value === "object" ? (value as Record<string, unknown>) : {};
      onCommit(field.key, { ...current, ...patch });
    },
    [field.key, value, onCommit],
  );

  // The builder state is local (seeded once from the persisted query — the
  // settings editors remount per instance, so no reset effect); the commit
  // effect below writes it back whenever it meaningfully changes.
  const query = useFilters(issueFilterConfig, {
    mode: "advanced",
    advancedState: (filters.query as FilterState | null | undefined) ?? SEEDED_EMPTY_QUERY,
  });

  // Persist the builder state per change: active filters store the whole state
  // (so reopening restores the built query), zero active filters store null
  // (half-built rows aren't worth sharing workspace-wide). The serialized
  // guard breaks the loop the round-trip would otherwise cause (commit →
  // `value` changes → `commitPatch` identity changes → effect re-runs).
  const lastCommittedQuery = useRef(JSON.stringify(filters.query ?? null));
  useEffect(() => {
    const next = countActiveFilters(query.advancedState) > 0 ? query.advancedState : null;
    const key = JSON.stringify(next);
    if (key === lastCommittedQuery.current) return;
    lastCommittedQuery.current = key;
    commitPatch({ query: next });
  }, [query.advancedState, commitPatch]);

  const clearQuery = useCallback(() => {
    query.clearAll();
    query.addBlock();
  }, [query]);

  const dateRange = filters.dateRange ?? null;
  const showArchived = filters.showArchived === true;
  const hideClosed = filters.hideClosed === true;

  return (
    <Box direction="row" gap="sm" align="center" wrap>
      <Popover>
        <PopoverTrigger
          render={
            <FilterToggle
              size="xs"
              icon="filter"
              count={query.activeFilterCount}
              disabled={disabled}
              onClear={clearQuery}
              tooltip="Filter this widget's Projects"
              clearTooltip="Clear all filters"
            />
          }
        />
        {/* No portalHost: Base UI portals to body and stacks nested layers
          * natively (`--nested-dialogs`), so this popover opened from inside
          * the settings dialog needs no shared host. */}
        <PopoverPopup align="start" padding="none" width="builder">
          <Box direction="column" maxHeight={440}>
            <ScrollArea>
              <AdvancedFilters config={issueFilterConfig} filters={query} scope={issueScope} />
            </ScrollArea>
          </Box>
        </PopoverPopup>
      </Popover>
      {/* Icon-only while the widget follows the page's date range; the preset
          label appears once the widget has a range of its own. Only the bare
          glyph carries a tooltip — it is the one place the inherited range is
          named — while a chosen preset speaks for itself on the face. */}
      <DateRangePresetPicker
        value={dateRange}
        onChange={(next) => commitPatch({ dateRange: next })}
        align="start"
      >
        <SelectTrigger chevron={false}
          // A combobox takes no name from its content (the preset label).
          aria-label="Date range"
          render={
            <FilterToggle
              size="xs"
              icon="calendar"
              label={dateRange ? formatPresetValueLabel(dateRange) : "Date range"}
              flag
              selected={!!dateRange}
              disabled={disabled}
              onClear={() => commitPatch({ dateRange: null })}
              tooltip={dateRange ? undefined : "Following the page's date range"}
              clearTooltip="Follow the page's date range"
            />
          }
        />
      </DateRangePresetPicker>
      {/* The Filter chip's own shape: the icon alone while off, the label
          revealed while archived rows are in. A press toggles it. */}
      <FilterToggle
        size="xs"
        icon="archive"
        label="Archived"
        flag
        selected={showArchived}
        disabled={disabled}
        onClick={() => commitPatch({ showArchived: !showArchived })}
        tooltip="Show archived Projects"
      />
      <FilterToggle
        size="xs"
        icon="check-circle"
        label="Hide Completed & Canceled"
        flag
        selected={hideClosed}
        disabled={disabled}
        onClick={() => commitPatch({ hideClosed: !hideClosed })}
        tooltip="Hide Projects in a Completed or Canceled stage"
      />
    </Box>
  );
}

/** Workspace custom fields as select options. */
function useCustomFieldsAsOptions(): WidgetSettingOption[] {
  const { data } = useCustomFieldsList(false);
  return useMemo(() => {
    const nodes = data?.customFields?.edges?.map((e) => e.node) ?? [];
    return nodes.map((cf) => ({ value: cf.customFieldId, label: cf.name }));
  }, [data]);
}

/** Workspace workflows as select options (the already-loaded workspace data — no
 *  extra query). */
function useWorkflowsAsOptions(): WidgetSettingOption[] {
  const { workflows } = useWorkspaceData();
  return useMemo(
    () => workflows.map((w) => ({ value: w.workflowId, label: w.name ?? "Untitled workflow" })),
    [workflows],
  );
}

/** Every live workflow's stages in stage order, grouped under the workflow's
 *  name, for the Projects by stage tile's Stage select. An archived
 *  workflow's stages are left out: a stored id that names one reads as
 *  missing, which the select words on its own (the tile reads it the same
 *  way). On a dashboard scoped to one workflow, only that workflow's stages
 *  are offered: the scope ANDs onto the stage, so any other stage counts 0. */
function useStagesAsOptions(): WidgetSettingOption[] {
  const { workflows, stagesByWorkflow } = useWorkspaceData();
  const scope = useDashboardScope();
  const scopedWorkflowId = scope?.entity?.type === "workflow" ? scope.entity.id : null;
  return useMemo(
    () =>
      workflows
        .filter((w) => !w.archived && (!scopedWorkflowId || w.workflowId === scopedWorkflowId))
        .flatMap((w) =>
          (stagesByWorkflow.get(w.workflowId) ?? []).map((s) => ({
            value: s.stageId,
            label: s.name,
            group: w.name ?? "Untitled workflow",
          })),
        ),
    [workflows, stagesByWorkflow, scopedWorkflowId],
  );
}

/** The stage name behind a stored stage id, for the title a tile derives
 *  ("Projects in Roof inspection") outside any settings drawer. */
function useStageOptionLabel(): (value: string) => string | undefined {
  const { stageMap } = useWorkspaceData();
  return useCallback((value: string) => stageMap.get(value)?.name, [stageMap]);
}
